export type ProjectSettings = {
    projectName: string;
    publicSiteUrl: string;
    defaultLanguage: string;
    adminApiBaseUrl: string;
    adminApiVersion: string;
    adminApiToken: string;
    destinationExamTaskApiController: string;
    destinationExamTaskPrimaryActionLabel: string;
    destinationExamTaskPrimaryActionPath: string;
    destinationExamTaskSecondaryActionLabel: string;
    destinationExamTaskSecondaryActionPath: string;
};

export const ADMIN_SETTINGS_STORAGE_KEY = 'pteultra-admin:settings';

const projectSettingsKeys = [
    'projectName',
    'publicSiteUrl',
    'defaultLanguage',
    'adminApiBaseUrl',
    'adminApiVersion',
    'adminApiToken',
    'destinationExamTaskApiController',
    'destinationExamTaskPrimaryActionLabel',
    'destinationExamTaskPrimaryActionPath',
    'destinationExamTaskSecondaryActionLabel',
    'destinationExamTaskSecondaryActionPath',
] as const satisfies readonly (keyof ProjectSettings)[];

function safeJsonParse<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

export function getDefaultProjectSettings(): ProjectSettings {
    return {
        projectName: 'PTE Ultra Admin',
        publicSiteUrl: '',
        defaultLanguage: 'en',
        adminApiBaseUrl: 'https://test-api.pteultra.com/api/v1',
        adminApiVersion: '1',
        adminApiToken: '',
        destinationExamTaskApiController: 'AdminExamTaskLiveEdit',
        destinationExamTaskPrimaryActionLabel: 'Save',
        destinationExamTaskPrimaryActionPath: 'update',
        destinationExamTaskSecondaryActionLabel: 'Unpublish',
        destinationExamTaskSecondaryActionPath: 'unpublish',
    };
}

function sanitizeProjectSettings(raw: Record<string, unknown> | null): ProjectSettings {
    const defaults = getDefaultProjectSettings();
    const sanitized = { ...defaults };

    if (!raw) {
        return sanitized;
    }

    for (const key of projectSettingsKeys) {
        const value = raw[key];
        if (typeof value === 'string') {
            sanitized[key] = value;
        }
    }

    return sanitized;
}

export function readProjectSettings(): ProjectSettings {
    const raw = localStorage.getItem(ADMIN_SETTINGS_STORAGE_KEY);
    const parsed = safeJsonParse<Record<string, unknown>>(raw);
    const sanitized = sanitizeProjectSettings(parsed);
    const serialized = JSON.stringify(sanitized);

    if (raw !== serialized) {
        localStorage.setItem(ADMIN_SETTINGS_STORAGE_KEY, serialized);
    }

    return sanitized;
}

export function writeProjectSettings(settings: ProjectSettings): void {
    localStorage.setItem(ADMIN_SETTINGS_STORAGE_KEY, JSON.stringify(sanitizeProjectSettings(settings)));
}